import { IS_DRAG_PREVIEW_ATTR } from 'base/ui/dnd';
import {
  makeObservable,
  observable,
  onBecomeObserved,
  onBecomeUnobserved,
} from 'mobx';

export const SELECTABLE_OR_FRAME_ID_ATTR = 'data-rlselection';
/* For a target T, both the original element and the selection frame overlay
 * will set SELECTABLE_OR_FRAME_ID_ATTR.  So we additionally add
 * SELECTABLE_ORIGINAL_ID_ATTR to just the original element.  This allows
 * us to measure the original element.
 **/
export const SELECTABLE_ORIGINAL_ID_ATTR = 'data-rlselectionoriginal';
/**
 * The element which the selection frame for target T should be drawn around.
 * Sometimes (Pages in Wireframe mode) this is different to the "original" element.
 **/
export const SELECTABLE_ATTACH_FRAME_ID_ATTR = 'data-rlframeattach';

/** Measures the position of a selection target (a selectable) */
class SelectionMeasurer {
  // Not observable (for simplicity of onBecomeUnobserved).  It is assumed that
  // users also observer the rect.
  el: HTMLElement | undefined = undefined;
  private elIsDragPreview = false;
  @observable.ref
  rect: DOMRect = ZERO_RECT;

  constructor(
    private readonly attrKey: string,
    private readonly id: string,
    private readonly resizeObserver: ResizeObserver,
  ) {
    makeObservable(this);
    this.maybeUpdateEl();
  }

  update() {
    this.maybeUpdateEl();
    if (!this.el || this.elIsDragPreview) return (this.rect = ZERO_RECT);
    const next = this.el.getBoundingClientRect();
    if (
      next.top === this.rect.top &&
      next.left === this.rect.left &&
      next.width === this.rect.width &&
      next.height === this.rect.height
    ) {
      return;
    }
    this.rect = next;
  }

  private maybeUpdateEl() {
    if (
      this.el?.getAttribute(this.attrKey) !== this.id ||
      !this.el.isConnected
    ) {
      this.el && this.resizeObserver.unobserve(this.el);
      this.el = undefined;
      this.elIsDragPreview = false;
    }
    if (!this.el) {
      const el = document.querySelector(`[${this.attrKey}="${this.id}"]`);
      if (el instanceof HTMLElement) {
        this.el = el;
        // Drag preview elements:
        //      Goal - hide selection hanger-on-ers (frame, buttons, etc.) while dragging.
        // But, it is really important that we update immediately when the drag preview
        // finished:
        //      You'd think we can do that more efficiently with a MutationObserver,
        //      but you'd be wrong.  MutationObserver doesn't fire on unmounts ;(
        //      So we keep the ResizeObserver on the drag preview, but we don't
        //      update the rect.
        this.elIsDragPreview = el.closest(`[${IS_DRAG_PREVIEW_ATTR}]`) != null;
        this.resizeObserver.observe(el);
      }
    }
  }

  destroy() {
    this.el && this.resizeObserver.unobserve(this.el);
  }
}

/** Measures the position of ALL selection targets (selectables) */
class SelectionsMeasurer {
  private readonly measurers = observable.map<string, SelectionMeasurer>([], {
    deep: false,
  });
  private readonly resizeObserver = new window.ResizeObserver(() => {
    // If one thing changed size it probably caused other things to move.
    // ResizeObserver doesn't fire on moves, so we update everything.
    this.updateAll();
  });

  get(area: 'frame' | 'selectable', id: string) {
    const attrKey =
      area === 'frame'
        ? SELECTABLE_ATTACH_FRAME_ID_ATTR
        : SELECTABLE_ORIGINAL_ID_ATTR;
    const key = `${attrKey}:${id}`;
    let m = this.measurers.get(key);
    if (!m) {
      m = new SelectionMeasurer(attrKey, id, this.resizeObserver);
      this.measurers.set(key, m);

      let destroyTimeout: number | undefined;
      onBecomeObserved(m, 'rect', () => {
        clearTimeout(destroyTimeout);
      });
      onBecomeUnobserved(m, 'rect', () => {
        // The delay is crucial here.  React unmounts things before it mounts,
        // so if we destroy immediately we might destroy something that is about
        // to be used by a new component.
        destroyTimeout = window.setTimeout(() => {
          m?.destroy();
          this.measurers.delete(key);
        }, 1000);
      });
    }
    return m;
  }

  invalidate(id: string) {
    for (const attrKey of [
      SELECTABLE_ORIGINAL_ID_ATTR,
      SELECTABLE_ATTACH_FRAME_ID_ATTR,
    ]) {
      const key = `${attrKey}:${id}`;
      const m = this.measurers.get(key);
      m && m.update();
    }
  }

  private updateAll() {
    for (const m of this.measurers.values()) {
      m.update();
    }
  }

  readonly onCanvasTransformOrSizeChange = () => {
    // Let's debounce this one day (once per frame?).  It's hard to get it
    // debounced without looking too delayed though.
    this.updateAll();
  };
}

export const globalSelectionsMeasurer = new SelectionsMeasurer();

const ZERO_RECT = new DOMRect(0, 0, 0, 0);
